/**
 * AdminCheckLiquidationButton — Round P119c (Feb 2026)
 *
 * One-click confirmation of a ticker's liquidation status. Fires
 * POST /api/admin/check-liquidation/{ticker} → confirms via Massive's
 * v3 tickers listing (active=false + delisted_utc). On success,
 * stock_cache.liquidated is set to True automatically and every
 * downstream banner (StreakRiskChart, holding-family routing)
 * flips to the definitive delisted state on the next data fetch.
 *
 * Admin-only. Renders as a compact violet pill button — safe to
 * drop into any admin surface (StockModal admin footer, Pending
 * Non-Dividend Review row, top movers audit table, etc.).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldQuestion } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCheckLiquidationButton({ ticker, onConfirmed, size = 'sm' }) {
  // Round P119c (Feb 2026) — Defense in depth. Backend endpoint
  // /api/admin/check-liquidation/{ticker} is already gated by
  // `role != "admin"` (returns 403). This second guard ensures the
  // BUTTON ITSELF never renders for non-admins even if a caller
  // forgets to wrap it in `{isAdmin && ...}`. Last summer's incident
  // where every user could see admin buttons was caused by a caller
  // dropping the wrapper; self-gating here means the component
  // fails safely on its own.
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  if (!user || user.role !== 'admin') return null;

  const run = async (e) => {
    e?.stopPropagation?.();
    if (!ticker || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/check-liquidation/${encodeURIComponent(ticker)}`,
        {},
        { withCredentials: true },
      );
      setLastResult(data);
      if (data.confirmed) {
        toast.success(`${ticker} confirmed liquidated`, {
          description: `Massive reports active=false${
            data.delisted_utc ? ` · delisted ${String(data.delisted_utc).slice(0, 10)}` : ''
          }${data.name ? ` · ${data.name}` : ''}. stock_cache updated — banner will flip on reload.`,
        });
        onConfirmed?.(data);
      } else {
        toast.info(`${ticker} NOT confirmed liquidated`, {
          description: 'Massive still lists this ticker as active. The softer "possible liquidation" warning stays until an admin resolves it manually.',
        });
      }
    } catch (err) {
      toast.error('Liquidation check failed', {
        description: err?.response?.data?.detail || String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const px = size === 'xs' ? 'px-1.5 py-0.5 text-[9.5px]' : 'px-2 py-1 text-[10.5px]';
  const iconSize = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3';

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !ticker}
      className={`inline-flex items-center gap-1 rounded border border-violet-400/60 bg-violet-500/10 font-bold uppercase tracking-wide text-violet-800 dark:text-violet-200 hover:bg-violet-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${px}`}
      data-testid={`admin-check-liquidation-${ticker}`}
      title={
        lastResult
          ? (lastResult.confirmed
            ? `Confirmed liquidated · delisted ${String(lastResult.delisted_utc || '').slice(0, 10)}`
            : 'Massive still shows this ticker as active — not liquidated.')
          : 'One-click confirmation via Massive. Sets stock_cache.liquidated=True on the ticker if delisted.'
      }
    >
      {lastResult?.confirmed ? (
        <ShieldCheck className={`${iconSize} text-emerald-600 dark:text-emerald-400`} />
      ) : (
        <ShieldQuestion className={iconSize} />
      )}
      {busy ? 'Checking…' : lastResult?.confirmed ? 'Confirmed' : 'Check Liquidation'}
    </button>
  );
}
